import { api } from "@utilities/api";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { env } from "@/env";

export type FlowRunStatus = "pending" | "generating" | "completed" | "failed";

export interface FlowRunOutput {
  id: string;
  status: FlowRunStatus;
  content_url?: string | null;
  mime_type?: string | null;
}

export interface FlowRunResponse {
  id: string;
  template_id: string;
  status: FlowRunStatus;
  outputs: Record<string, FlowRunOutput>;
}

/**
 * SSE `status_update` event payload — mirrors xi's
 * `ContentGenerationStatusUpdateResponseModel`. xi keys events by
 * `template_node_id` (the source node), which we map back to an output port via
 * `FlowTemplatePort.source_node_id`. `id` is the generation id.
 */
export interface ContentGenerationStatusUpdate {
  id: string;
  generation_session_id: string;
  status: FlowRunStatus;
  template_node_id?: string | null;
  content_url?: string | null;
  content_capped_resolution_url?: string | null;
  preview_content_url?: string | null;
  thumbnail_url?: string | null;
  error_message?: string | null;
  failure_reason?: string | null;
}

export type ReferenceValue =
  | { content_asset_id: string }
  | { generation_id: string }
  | { template_node_id: string }
  | { inline_text: string };

/**
 * Trigger an anonymous flow template run. The marketing site uploads the input
 * asset via `uploadCreativeTemplateAsset` (see `creativeTemplates.ts` — the same
 * endpoint is reused for both surfaces). The returned workflow `asset_id` is
 * passed here as `content_asset_id`; the backend transparently promotes it to
 * a real ContentAsset before running.
 */
export async function runFlowTemplate({
  templateId,
  inputs,
  signal,
}: {
  templateId: string;
  inputs: Record<string, ReferenceValue>;
  signal?: AbortSignal;
}): Promise<FlowRunResponse> {
  const hcaptchaToken = await getCaptchaToken({
    behaviourType: "creative_template",
  });

  return api.post<FlowRunResponse>(
    `/v1/templates/anonymous/${encodeURIComponent(templateId)}/runs`,
    {
      inputs,
      hcaptcha_token: hcaptchaToken,
    },
    { signal },
  );
}

/**
 * Open an SSE stream that emits status updates for a single anonymous run. The
 * caller closes the EventSource when the run terminates (status COMPLETED or
 * FAILED) or to abandon early.
 */
export async function streamFlowTemplateRun({
  templateId,
  sessionId,
}: {
  templateId: string;
  sessionId: string;
}): Promise<EventSource> {
  const hcaptchaToken = await getCaptchaToken({
    behaviourType: "creative_template",
  });
  const url = new URL(
    `/v1/templates/anonymous/${encodeURIComponent(templateId)}/runs/${encodeURIComponent(sessionId)}/stream`,
    env.NEXT_PUBLIC_XI_SERVER_URL,
  );
  url.searchParams.set("hcaptcha_token", hcaptchaToken);
  return new EventSource(url.toString());
}
